import isEqual from 'lodash/isEqual'
import type {
  ChartCardState,
  ChartCardStateInitChartAction,
  ChartCardStateInitTableAction,
  ChartCardStateResetSelectionsAction,
  ChartCardStateUpdateDateRangeAction,
  ChartCardStateResetZoomingAction,
  ChartCardStateSelectionAction,
  ChartCardStateUpdateSelectionsAction,
  ChartCardStateValid,
  ChartCardStateZoomInAction,
  ChartChoice,
  ChartColumnDimensionValuesChoice,
  ChartDimensionChoice,
  ChartDimensionValueChoice,
  ChartDimensionValueOfChoice,
  ChartDimensionValuesChoice,
  ChartDimensionValuesOfChoice,
  ChartMetricChoice,
  ChartMetricsChoice,
  ChartMetricTransposedChoice,
  ChartMetricVariantsChoice,
  ChartCardTableColumnsChoice,
  ChartVariantValueChoice,
  ChartVariantValuesChoice,
  ChartZoomedIn,
  ChoiceFilter,
  DefaultSelections,
  DefaultSelectionValue,
  DimensionAxisType,
  DimensionsMap,
  SelectionValuesMap,
  ChartCardStateCheckCardTypeAction
} from '../internalTypes'
import type {
  BaseElementDescription,
  ChartChoiceOptions,
  ChartChoiceOptionsOf,
  ChartDataRow,
  ChartSelections,
  DataKeyMap,
  Dimension,
  DimensionString,
  MultipleSelectionValue,
  SelectionValue
} from '../types'
import {
  assertNever,
  getIdFromSelectionOthersId,
  generateIdForSelectionOthers,
  isSelectionOthersId,
  errorLocal,
  objectKeys
} from './generics'
import { NotSelectedOthers, SelectedOthers, TableColumnsChoice, TableColumnValuesChoice } from '../constants'

const generateDefaultChartSelectionsReducer = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  acc: DefaultSelections<DataKey>,
  choiceId: DataKey,
  choicesMap: Record<DataKey, ChartChoice<DataKey, DataRow>>
): DefaultSelections<DataKey> => {
  const choice = choicesMap[choiceId]
  if (choice == null) {
    return acc
  }
  const { type, change } = choice
  switch (type) {
    case 'metric':
      acc[choiceId] = change.options[0].value
      if (choice.variant != null) {
        generateDefaultChartSelectionsReducer(acc, choice.variant, choicesMap)
      }
      break
    case 'metricVariants':
      acc[choiceId] = change.options[0].value
      generateDefaultChartSelectionsReducer(acc, choice.variant, choicesMap)
      if (choice.transpose != null) {
        generateDefaultChartSelectionsReducer(acc, choice.transpose, choicesMap)
      }
      break
    case 'dimension':
    case 'value':
      acc[choiceId] = change.options[0].value
      break
    case 'metricTransposed':
      acc[choiceId] = change.options[0].value
      if (choice.variant != null) {
        generateDefaultChartSelectionsReducer(acc, choice.variant, choicesMap)
      }
      generateDefaultChartSelectionsReducer(acc, choice.transpose, choicesMap)
      acc[generateIdForSelectionOthers(choiceId)] = NotSelectedOthers
      break
    case 'valueOf': {
      generateDefaultChartSelectionsReducer(acc, choice.dimension, choicesMap)
      const { options } = change
      acc[choiceId] = objectKeys(options).reduce<SelectionValuesMap<DataKey>>((acc, key) => {
        acc[key] = options[key][0].value
        return acc
      }, {})
      break
    }
    case 'metrics': {
      const optionValues = change.options.map((option) => option.value)
      const optionSet = new Set(optionValues)
      let defaults = choice.selected == null ? optionValues : choice.selected.filter((value) => optionSet.has(value))
      if (defaults.length === 0) {
        defaults = optionValues
      }
      if (choice.limit != null) {
        defaults = defaults.slice(0, choice.limit)
      }
      acc[choiceId] = defaults
      if (choice.variant != null) {
        generateDefaultChartSelectionsReducer(acc, choice.variant, choicesMap)
      }
      break
    }
    case 'values':
    case TableColumnValuesChoice:
      if (choice.limit == null) {
        acc[choiceId] = change.options.map((option) => option.value)
      } else {
        acc[choiceId] = change.options.map((option) => option.value).slice(0, choice.limit)
      }
      acc[generateIdForSelectionOthers(choiceId)] = NotSelectedOthers
      break
    case 'variantValue':
      acc[choiceId] = change.options[0].value
      break
    case 'variantValues':
      acc[choiceId] = change.options.map((option) => option.value)
      break
    case 'valuesOf': {
      generateDefaultChartSelectionsReducer(acc, choice.dimension, choicesMap)
      const { options } = change
      acc[choiceId] = objectKeys(options).reduce<SelectionValuesMap<DataKey>>((acc, key) => {
        const values = options[key].map((option) => option.value)
        if (choice.limit != null) {
          acc[key] = values.slice(0, choice.limit)
        } else {
          acc[key] = values
        }
        return acc
      }, {})
      acc[generateIdForSelectionOthers(choiceId)] = NotSelectedOthers
      break
    }
    case TableColumnsChoice: {
      const optionValues = change.options.map((option) => option.value)
      const optionSet = new Set(optionValues)
      let defaults = choice.selected == null ? optionValues : choice.selected.filter((value) => optionSet.has(value))
      if (defaults.length === 0) {
        defaults = optionValues
      }
      acc[choiceId] = defaults
      break
    }
    default:
      assertNever(type)
  }
  return acc
}

export const generateDefaultChartSelections = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  choicesList: DataKey[],
  choicesMap: Record<DataKey, ChartChoice<DataKey, DataRow>>
): DefaultSelections<DataKey> | undefined => {
  if (choicesList == null || choicesMap == null) {
    return undefined
  }

  return choicesList.reduce<DefaultSelections<DataKey>>(
    (acc, choiceId) => generateDefaultChartSelectionsReducer<DataKey, DataRow>(acc, choiceId, choicesMap),
    {}
  )
}

const filterIsStringFiltered = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  dimension: Dimension<'choice', DataKey, DataRow> | undefined
): dimension is DimensionString<'choice', DataKey, DataRow> =>
  dimension?.type === 'string' && dimension.filter != null && (dimension.filter === true || dimension.filter.length > 0)

export const generateDefaultFiltersFromDimensions = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  dimensionsMap: DimensionsMap<DataKey, DataRow>
): Array<ChoiceFilter<DataKey>> =>
  objectKeys(dimensionsMap)
    .map((key) => dimensionsMap[key])
    .filter(filterIsStringFiltered)
    .map(({ id, filter, descriptions }) => ({
      id,
      value:
        filter === true ? objectKeys(descriptions as Record<string, BaseElementDescription>) : (filter as string[]),
      clause: 'in'
    }))

const checkSingleSelectionAgainstOptions = <DataKey extends string, Option extends string>({
  selected,
  options,
  otherOption,
  otherSelected
}:
  | {
      selected: SelectionValue<DataKey> | undefined
      options: ChartChoiceOptions<Option>
      otherOption: Option | undefined
      otherSelected?: never
    }
  | {
      selected: SelectionValue<DataKey> | undefined
      options: ChartChoiceOptionsOf<DataKey, Option>
      otherOption: Option | undefined
      otherSelected: SelectionValue<DataKey>
    }): boolean => {
  if (Array.isArray(selected)) {
    return false
  }
  if (selected == null) {
    return false
  }
  if (selected === otherOption) {
    return true
  }
  if (Array.isArray(options)) {
    return options.some(({ value }) => value === selected)
  }
  if (otherSelected == null) {
    return false
  }
  if (typeof otherSelected !== 'string') {
    return false
  }
  if (otherSelected in options) {
    const optionsOf = options[otherSelected as DataKey]
    return optionsOf.some(({ value }) => value === selected)
  }
  return false
}

const checkMultipleSelectionAgainstOptions = <DataKey extends string, Option extends string>({
  selected,
  options,
  otherOption,
  otherSelected
}:
  | {
      selected: SelectionValue<DataKey> | undefined
      options: ChartChoiceOptions<Option>
      otherOption: Option | undefined
      otherSelected?: never
    }
  | {
      selected: SelectionValue<DataKey> | undefined
      options: ChartChoiceOptionsOf<DataKey, Option>
      otherOption: Option | undefined
      otherSelected: SelectionValue<DataKey>
    }): boolean => {
  if (typeof selected === 'string') {
    return false
  }
  if (selected == null) {
    return false
  }
  if (Array.isArray(options)) {
    return selected.every((option) => option === otherOption || options.some(({ value }) => value === option))
  }
  if (otherSelected == null) {
    return false
  }
  if (typeof otherSelected !== 'string') {
    return false
  }
  if (otherSelected in options) {
    const optionsOf = options[otherSelected as DataKey]
    return selected.every((option) => option === otherOption || optionsOf.some(({ value }) => value === option))
  }
  return false
}

const generateSingleSelection = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choice,
  selectionKey,
  defaultSelections,
  selections,
  newSelections
}: {
  choice:
    | ChartMetricChoice<DataKey, DataRow>
    | ChartMetricVariantsChoice<DataKey, DataRow>
    | ChartMetricTransposedChoice<DataKey, DataRow>
    | ChartVariantValueChoice<DataKey, DataRow>
    | ChartDimensionChoice<DataKey, DataRow>
    | ChartDimensionValueChoice<DataKey, DataRow>
  selectionKey: DataKey
  defaultSelections: DefaultSelections<DataKey>
  selections: ChartSelections<DataKey>
  newSelections: ChartSelections<DataKey>
}): void => {
  const { type, change } = choice
  const selected = selections[selectionKey]
  const { options, otherOption } = change

  if (selected == null) {
    newSelections[selectionKey] = defaultSelections[selectionKey] as DataKey
  } else {
    if (typeof selected !== 'string') {
      errorLocal(`generateSelections ${type} choice ${selectionKey} selected not a string`, {
        selected,
        selections
      })
      return undefined
    }
    if (!checkSingleSelectionAgainstOptions({ selected, options, otherOption })) {
      errorLocal(`generateSelections ${type} choice ${selectionKey} selected not in options`, {
        selected,
        options,
        otherOption
      })
      return undefined
    }
    newSelections[selectionKey] = selected
  }
}

const generateMetricsOrTableColumnsSelection = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choice,
  selectionKey,
  defaultSelections,
  selections,
  newSelections
}: {
  choice: ChartMetricsChoice<DataKey, DataRow> | ChartCardTableColumnsChoice<DataKey, DataRow>
  selectionKey: DataKey
  defaultSelections: DefaultSelections<DataKey>
  selections: ChartSelections<DataKey>
  newSelections: ChartSelections<DataKey>
}): void => {
  const { type, change } = choice
  const selected = selections[selectionKey]
  const { options } = change

  if (selected == null) {
    newSelections[selectionKey] = defaultSelections[selectionKey] as SelectionValue<DataKey>
  } else {
    if (!Array.isArray(selected)) {
      errorLocal(`generateSelections ${type} choice ${selectionKey} selected invalid`, {
        selected,
        selections
      })
      return undefined
    }
    if (!checkMultipleSelectionAgainstOptions<DataKey, DataKey>({ selected, options, otherOption: undefined })) {
      errorLocal(`generateSelections ${type} choice ${selectionKey} selected not in options`, {
        selected,
        options
      })
      return undefined
    }
    if (type === 'metrics' && choice.limit != null) {
      newSelections[selectionKey] = selected.slice(0, choice.limit)
    } else {
      newSelections[selectionKey] = selected
    }
  }
}

const generateValuesSelection = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choice,
  selectionKey,
  defaultSelections,
  selections,
  newSelections
}: {
  choice:
    | ChartVariantValuesChoice<DataKey, DataRow>
    | ChartDimensionValuesChoice<DataKey, DataRow>
    | ChartColumnDimensionValuesChoice<DataKey, DataRow>
  selectionKey: DataKey
  defaultSelections: DefaultSelections<DataKey>
  selections: ChartSelections<DataKey>
  newSelections: ChartSelections<DataKey>
}): void => {
  const { type, change } = choice
  const selected = selections[selectionKey]
  const { options, otherOption } = change

  if (selected == null) {
    newSelections[selectionKey] = defaultSelections[selectionKey] as SelectionValue<DataKey>
  } else {
    if (!Array.isArray(selected)) {
      errorLocal(`generateSelections ${type} choice ${selectionKey} selected not invalid`, {
        selected,
        selections
      })
      return undefined
    }
    if (!checkMultipleSelectionAgainstOptions<DataKey, string>({ selected, options, otherOption })) {
      errorLocal(`generateSelections ${type} choice ${selectionKey} selected not in options`, {
        selected,
        options,
        otherOption
      })
      return undefined
    }
    if (type === 'values' && choice.limit != null) {
      newSelections[selectionKey] = selected.slice(0, choice.limit)
    }
    newSelections[selectionKey] = selected
  }
}

const generateValueOfSelection = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choice,
  selectionKey,
  defaultSelections,
  selections,
  newSelections
}: {
  choice: ChartDimensionValueOfChoice<DataKey, DataRow>
  selectionKey: DataKey
  defaultSelections: DefaultSelections<DataKey>
  selections: ChartSelections<DataKey>
  newSelections: ChartSelections<DataKey>
}): void => {
  const { type, change, dimension } = choice
  const selected = selections[selectionKey]
  const { options, otherOption } = change
  const otherSelected = newSelections[dimension] ?? selections[dimension]
  const defaultOtherSelected = defaultSelections[dimension] as DataKey
  const initialMap = defaultSelections[selectionKey] as Record<DataKey, string>

  if (otherSelected != null) {
    if (typeof otherSelected !== 'string') {
      errorLocal(`generateSelections ${type} choice ${selectionKey} otherSelected not a string`, {
        otherSelected,
        dimension,
        selections,
        newSelections
      })
      return undefined
    }
    if (otherSelected in initialMap) {
      newSelections[dimension] = otherSelected
    } else {
      errorLocal(`generateSelections ${type} choice ${selectionKey} otherSelected not in initialMap`, {
        otherSelected,
        dimension,
        selections,
        newSelections
      })
      return undefined
    }
  }

  if (selected == null) {
    if (otherSelected != null) {
      newSelections[selectionKey] = initialMap[otherSelected as DataKey] as SelectionValue<DataKey>
    } else {
      newSelections[dimension] = defaultOtherSelected
      newSelections[selectionKey] = initialMap[defaultOtherSelected as DataKey] as SelectionValue<DataKey>
    }

    return
  }

  if (typeof selected !== 'string') {
    errorLocal(`generateSelections ${type} choice ${selectionKey} selected not a string`, {
      selected,
      selections
    })
    return undefined
  }
  if (
    !checkSingleSelectionAgainstOptions<DataKey, string>({
      selected,
      options,
      otherOption,
      otherSelected: otherSelected ?? defaultOtherSelected
    })
  ) {
    errorLocal(`generateSelections ${type} choice ${selectionKey} selected not in options`, {
      selected,
      options,
      otherOption,
      otherSelected,
      dimension,
      selections,
      newSelections
    })
    return undefined
  }

  newSelections[selectionKey] = selected
  if (otherSelected == null) {
    newSelections[dimension] = defaultOtherSelected
  }
}

const generateValuesOfSelection = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choice,
  selectionKey,
  defaultSelections,
  selections,
  newSelections
}: {
  choice: ChartDimensionValuesOfChoice<DataKey, DataRow>
  selectionKey: DataKey
  defaultSelections: DefaultSelections<DataKey>
  selections: ChartSelections<DataKey>
  newSelections: ChartSelections<DataKey>
}): void => {
  const { type, change, dimension } = choice
  const { options, otherOption } = change
  const selected = selections[selectionKey]
  const otherSelected = newSelections[dimension] ?? selections[dimension]
  const defaultOtherSelected = defaultSelections[dimension] as DataKey
  const initialMap = defaultSelections[selectionKey] as Record<DataKey, string[]>

  if (otherSelected != null) {
    if (typeof otherSelected !== 'string') {
      errorLocal(`generateSelections ${type} choice ${selectionKey} otherSelected not a string`, {
        otherSelected,
        dimension,
        selections,
        newSelections
      })
      return
    }

    if (!(otherSelected in initialMap)) {
      errorLocal(`generateSelections ${type} choice ${selectionKey} otherSelected not in initialMap`, {
        otherSelected,
        dimension,
        selections,
        newSelections
      })
      return
    }

    newSelections[dimension] = otherSelected
  }

  if (selected == null) {
    if (otherSelected == null) {
      newSelections[dimension] = defaultOtherSelected
      newSelections[selectionKey] = initialMap[defaultOtherSelected as DataKey] as string[]
    } else {
      newSelections[selectionKey] = initialMap[otherSelected as DataKey] as string[]
    }

    return
  }

  if (!Array.isArray(selected)) {
    errorLocal(`generateSelections ${type} choice ${selectionKey} selected invalid`, {
      selected,
      selections
    })
    return
  }

  if (
    !checkMultipleSelectionAgainstOptions<DataKey, string>({
      selected,
      options,
      otherOption,
      otherSelected: otherSelected ?? defaultOtherSelected
    })
  ) {
    errorLocal(`generateSelections ${type} choice ${selectionKey} selected not in options`, {
      selected,
      options,
      otherOption,
      otherSelected,
      dimension,
      selections,
      newSelections
    })
    return
  }

  if (type === 'valuesOf' && choice.limit != null) {
    newSelections[selectionKey] = selected.slice(0, choice.limit)
  } else {
    newSelections[selectionKey] = selected
  }

  if (otherSelected == null) {
    newSelections[dimension] = defaultOtherSelected
  }
}

const generateSelection = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  selectionKey,
  choicesMap,
  selections,
  defaultSelections,
  newSelections
}: {
  selectionKey: DataKey
  choicesMap: Record<DataKey, ChartChoice<DataKey, DataRow>>
  selections: ChartSelections<DataKey>
  defaultSelections: DefaultSelections<DataKey>
  newSelections: ChartSelections<DataKey>
}): void => {
  const isOthers = isSelectionOthersId(selectionKey)
  const choiceId = isOthers ? getIdFromSelectionOthersId(selectionKey) : selectionKey
  const choice = choicesMap[choiceId]

  if (choice == null) {
    errorLocal(`generateSelections choice ${choiceId} null`, { key: selectionKey, choiceId, choicesMap })
    return undefined
  }

  const { type } = choice
  const selected = selections[selectionKey]

  if (isOthers) {
    if (selected != null && selected !== SelectedOthers && selected !== NotSelectedOthers) {
      errorLocal(
        `generateSelections ${type} choice ${selectionKey} selected not ${SelectedOthers} or ${NotSelectedOthers}`,
        {
          choiceId,
          choice,
          selectionKey,
          selected,
          selections,
          defaultSelections
        }
      )
      return undefined
    }
    newSelections[selectionKey] = selected === SelectedOthers ? SelectedOthers : NotSelectedOthers

    return
  }

  switch (type) {
    case 'metric':
    case 'metricVariants':
    case 'dimension':
    case 'metricTransposed':
    case 'value':
    case 'variantValue':
      generateSingleSelection({ choice, selectionKey, defaultSelections, selections, newSelections })
      break
    case 'metrics':
    case TableColumnsChoice:
      generateMetricsOrTableColumnsSelection({ choice, selectionKey, defaultSelections, selections, newSelections })
      break
    case 'values':
    case TableColumnValuesChoice:
    case 'variantValues':
      generateValuesSelection({ choice, selectionKey, defaultSelections, selections, newSelections })
      break
    case 'valueOf': {
      generateValueOfSelection({ choice, selectionKey, defaultSelections, selections, newSelections })
      break
    }
    case 'valuesOf':
      generateValuesOfSelection({ choice, selectionKey, defaultSelections, selections, newSelections })
      break
    default:
      assertNever(type)
  }
}

const generateSelections = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  selections: ChartSelections<DataKey>,
  choicesMap: Record<DataKey, ChartChoice<DataKey, DataRow>>,
  defaultSelections: DefaultSelections<DataKey>
): ChartSelections<DataKey> | undefined => {
  const newSelections: ChartSelections<DataKey> = {}

  for (const selectionKey of objectKeys(defaultSelections)) {
    generateSelection({
      selectionKey,
      choicesMap,
      selections,
      defaultSelections,
      newSelections
    })
  }

  return newSelections
}

const cascadeSelectionUpdate = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  type,
  id,
  selection,
  selectOthers,
  state
}: {
  type: ChartCardStateSelectionAction<DataKey, DataRow>['type']
  id: DataKey
  selection: DefaultSelectionValue<DataKey>
  selectOthers: boolean | undefined
  state: ChartCardStateValid<DataKey, DataRow>
}): ChartCardState<DataKey, DataRow> => {
  const {
    choicesList,
    choicesMap,
    dimensionAxis,
    defaultSelections,
    defaultFilters,
    selections: previousSelections,
    filters: previousFilters
  } = state

  if (typeof selection !== 'string' && !Array.isArray(selection)) {
    // Reset to default is not yet implemented for choice values
    // When implemented, should reset the valuesOf selection to its default state
    return state
  }

  const updatedSelections: ChartSelections<DataKey> = { ...previousSelections }

  const idForSelectionOthers = generateIdForSelectionOthers(id)

  updatedSelections[id] = selection
  if (selectOthers != null) {
    updatedSelections[idForSelectionOthers] = selectOthers ? SelectedOthers : NotSelectedOthers
  }

  const choice = choicesMap[id] as ChartChoice<DataKey, DataRow>
  const { type: choiceType, id: choiceId } = choice

  // Reset to default the depending selections.
  if (choiceType === 'dimension') {
    const dependingChoice = Object.values<ChartChoice<DataKey, DataRow>>(choicesMap).find(
      (choice) => (choice.type === 'valueOf' || choice.type === 'valuesOf') && choice.dimension === choiceId
    )
    if (dependingChoice != null) {
      updatedSelections[dependingChoice.id] = (
        defaultSelections[dependingChoice.id] as DataKeyMap<DataKey, SelectionValue<DataKey>>
      )[selection as DataKey]
    }
  }

  const selections = generateSelections<DataKey, DataRow>(updatedSelections, choicesMap, defaultSelections)
  if (selections == null) {
    errorLocal('cascadeSelectionUpdate generateSelections null selections', {
      type,
      id,
      selection,
      selectOthers,
      previousSelections,
      updatedSelections,
      choicesMap,
      defaultSelections
    })
    return {
      ...state,
      state: 'invalid'
    }
  }

  const selectionsUpdated = !isEqual(defaultSelections, selections)

  const filters = generateFiltersFromChoices<DataKey, DataRow>({
    selections,
    choicesList,
    choicesMap,
    dimensionAxis,
    defaultFilters
  })
  if (filters == null) {
    errorLocal('cascadeSelectionUpdate generateFilters null filters', {
      type,
      id,
      selection,
      selectOthers,
      selections,
      previousSelections,
      updatedSelections,
      choicesMap,
      defaultSelections
    })
    return {
      ...state,
      state: 'invalid'
    }
  }

  if (isEqual(filters, previousFilters)) {
    return {
      ...state,
      selections,
      selectionsUpdated
    }
  }

  return {
    ...state,
    filters,
    selections,
    selectionsUpdated
  }
}

const geberateFiltersForMetricTransposed = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  choice: ChartMetricTransposedChoice<DataKey, DataRow>,
  selections: ChartSelections<DataKey>,
  choicesMap: Record<DataKey, ChartChoice<DataKey, DataRow>>,
  filters: Array<ChoiceFilter<DataKey>>
): boolean => {
  const { transpose, type } = choice

  const transposeChoice = choicesMap[transpose]
  if (transposeChoice?.type === 'dimension') {
    return false
  }

  if (transposeChoice.type !== 'valuesOf' && transposeChoice.type !== 'values') {
    errorLocal(`generateFilters ${type} invalid transpose choice`, {
      choice,
      transpose,
      transposeChoice,
      selections
    })
    return true
  }

  const selected = selections[transpose]
  const othersSelected = selections[generateIdForSelectionOthers(transpose)] === SelectedOthers
  if (!Array.isArray(selected)) {
    errorLocal(`generateFilters ${type} selection for ${transpose} invalid`, {
      selected,
      selections
    })
    return true
  }
  const dimension = transposeChoice.dimension

  if (transposeChoice.type === 'valuesOf') {
    const selectedDimension = dimension in choicesMap ? selections[dimension] : dimension
    if (typeof selectedDimension !== 'string') {
      errorLocal(`generateFilters ${type} selectedDimension not a string`, {
        choice,
        transpose,
        transposeChoice,
        selectedDimension,
        selections
      })
      return true
    }
    const selectedFilter = othersSelected
      ? transposeChoice.change.options[dimension].map(({ value }) => value)
      : selected
    filters.push({
      id: selectedDimension as DataKey,
      value: selectedFilter,
      clause: 'in'
    })
    return false
  }

  return false
}

const generateFiltersForValues = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  choice:
    | ChartDimensionValuesChoice<DataKey, DataRow>
    | ChartColumnDimensionValuesChoice<DataKey, DataRow>
    | ChartVariantValuesChoice<DataKey, DataRow>,
  selections: ChartSelections<DataKey>,
  filters: Array<ChoiceFilter<DataKey>>
): boolean => {
  const { id, type, dimension } = choice

  const selectedOthers = selections[generateIdForSelectionOthers(id)]
  const selected = selections[id]
  if (!Array.isArray(selected)) {
    errorLocal(`generateFilters ${type} selection for ${id} invalid`, {
      selected,
      selections
    })
    return true
  }

  if (selectedOthers !== SelectedOthers) {
    filters.push({
      id: dimension,
      value: selected,
      clause: 'in'
    })
  }

  return false
}

const generateFiltersForValuesOf = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  choice: ChartDimensionValuesOfChoice<DataKey, DataRow>,
  choicesMap: Record<DataKey, ChartChoice<DataKey, DataRow>>,
  selections: ChartSelections<DataKey>,
  filters: Array<ChoiceFilter<DataKey>>
): boolean => {
  const { id, type, dimension } = choice

  const selectedDimension = dimension in choicesMap ? (selections[dimension] as DataKey) : dimension
  const selectedOthers = selections[generateIdForSelectionOthers(id)]
  const selected = selections[id]
  if (!Array.isArray(selected)) {
    errorLocal(`generateFilters ${type} selection for ${selectedDimension} invalid`, {
      selected,
      selections
    })
    return true
  }

  if (selectedOthers !== SelectedOthers) {
    filters.push({
      id: selectedDimension,
      value: selected,
      clause: 'in'
    })
  }

  return false
}

const generateFiltersFromChoice = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  choice,
  selections,
  choicesMap,
  dimensionAxis,
  filters
}: {
  choice: ChartChoice<DataKey, DataRow>
  selections: ChartSelections<DataKey>
  choicesMap: Record<DataKey, ChartChoice<DataKey, DataRow>>
  dimensionAxis: DimensionAxisType
  filters: Array<ChoiceFilter<DataKey>>
}): void => {
  const { type, axis } = choice
  switch (type) {
    case 'metricTransposed':
      if (geberateFiltersForMetricTransposed(choice, selections, choicesMap, filters)) {
        return undefined
      }
      break
    case 'values':
      if (generateFiltersForValues(choice, selections, filters)) {
        return undefined
      }
      break
    case 'variantValues':
      if (axis === dimensionAxis) {
        if (generateFiltersForValues(choice, selections, filters)) {
          return undefined
        }
      }
      break
    case 'valuesOf':
      if (generateFiltersForValuesOf(choice, choicesMap, selections, filters)) {
        return undefined
      }
      break
    case 'metric':
    case 'metricVariants':
    case 'metrics':
    case 'value':
    case 'valueOf':
    case 'variantValue':
    case 'dimension':
    case TableColumnsChoice:
    case TableColumnValuesChoice:
      // Those choices do not need data filtering.
      break
    default:
      assertNever(type)
  }
}

const generateFiltersFromChoices = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  selections,
  choicesList,
  choicesMap,
  dimensionAxis,
  defaultFilters
}: {
  selections: ChartSelections<DataKey>
  choicesList: DataKey[]
  choicesMap: Record<DataKey, ChartChoice<DataKey, DataRow>>
  dimensionAxis: DimensionAxisType
  defaultFilters: Array<ChoiceFilter<DataKey>>
}): Array<ChoiceFilter<DataKey>> | undefined => {
  const filters: Array<ChoiceFilter<DataKey>> = [...defaultFilters]

  // Process choices to add needed filtering.
  // Only choices that involve Dimension Values need data filtering.
  for (const choiceId of choicesList) {
    generateFiltersFromChoice({
      choice: choicesMap[choiceId],
      selections,
      choicesMap,
      dimensionAxis,
      filters
    })
  }

  return filters
}

export const stateReducerCheckCardTypeAction = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  state: ChartCardState<DataKey, DataRow>,
  action: ChartCardStateCheckCardTypeAction<DataKey, DataRow>
): ChartCardState<DataKey, DataRow> => {
  const { state: prevState } = state
  if (prevState === 'initialising' || prevState === 'invalid') {
    return state
  }
  const { cardType } = action
  if (state.cardType === cardType) {
    return state
  }
  return { state: 'initialising' }
}

export const stateReducerInitTableAction = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  state: ChartCardState<DataKey, DataRow>,
  {
    props: { result: chartPropsValidity, columns, choicesList, choicesMap, dimensionsMap }
  }: ChartCardStateInitTableAction<DataKey, DataRow>
): ChartCardState<DataKey, DataRow> => {
  const { state: prevState } = state

  if (chartPropsValidity !== 'validTable') {
    errorLocal('stateReducerInitTableAction invalid props', chartPropsValidity)
    return {
      ...state,
      state: 'invalid'
    }
  }

  const defaultSelections = generateDefaultChartSelections<DataKey, DataRow>(choicesList, choicesMap)

  if (defaultSelections == null) {
    errorLocal('stateReducerInitAction null defaultSelections')
    return {
      ...state,
      state: 'invalid'
    }
  }

  const defaultFilters = generateDefaultFiltersFromDimensions<DataKey, DataRow>(dimensionsMap)

  if (prevState === 'valid' || prevState === 'initialised') {
    if (
      state.cardType === 'table' &&
      isEqual(state.defaultSelections, defaultSelections) &&
      isEqual(state.defaultFilters, defaultFilters) &&
      isEqual(state.choicesList, choicesList) &&
      isEqual(state.choicesMap, choicesMap)
    ) {
      return state
    }
  }

  return {
    ...state,
    state: 'initialised',
    cardType: 'table',
    choicesList,
    choicesMap,
    defaultSelections,
    defaultFilters,
    dimensionAxis: 'first'
  }
}

export const stateReducerInitChartAction = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  state: ChartCardState<DataKey, DataRow>,
  {
    props: { result: chartPropsValidity, choicesList, choicesMap, dimensionAxis, dimensionsMap }
  }: ChartCardStateInitChartAction<DataKey, DataRow>
): ChartCardState<DataKey, DataRow> => {
  const { state: prevState } = state

  if (chartPropsValidity !== 'validChart') {
    errorLocal('stateReducerInitChartAction invalid props', chartPropsValidity)
    return {
      ...state,
      state: 'invalid'
    }
  }

  const defaultSelections = generateDefaultChartSelections<DataKey, DataRow>(choicesList, choicesMap)

  if (defaultSelections == null) {
    errorLocal('stateReducerInitAction null defaultSelections')
    return {
      ...state,
      state: 'invalid'
    }
  }

  const defaultFilters = generateDefaultFiltersFromDimensions<DataKey, DataRow>(dimensionsMap)

  if (prevState === 'valid' || prevState === 'initialised') {
    if (
      state.cardType === 'chart' &&
      isEqual(state.defaultSelections, defaultSelections) &&
      isEqual(state.defaultFilters, defaultFilters) &&
      isEqual(state.choicesList, choicesList) &&
      isEqual(state.choicesMap, choicesMap) &&
      state.dimensionAxis === dimensionAxis
    ) {
      return state
    }
  }

  return {
    ...state,
    state: 'initialised',
    cardType: 'chart',
    choicesList,
    choicesMap,
    defaultSelections,
    defaultFilters,
    dimensionAxis
  }
}

export const stateReducerUpdateSelectionsAction = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  state: ChartCardState<DataKey, DataRow>,
  {
    type,
    selections: selectionsProp
  }: ChartCardStateUpdateSelectionsAction<DataKey, DataRow> | ChartCardStateResetSelectionsAction<DataKey, DataRow>
): ChartCardState<DataKey, DataRow> => {
  const {
    state: prevState,
    choicesList,
    choicesMap,
    dimensionAxis,
    defaultSelections,
    defaultFilters,
    selections: prevSelections,
    zoomedIn,
    firstAxisDomainDataKey,
    firstAxisDomain,
    secondAxisDomains
  } = state

  const selectionsInput = type === 'resetSelections' ? {} : selectionsProp

  switch (prevState) {
    case 'invalid':
    case 'initialising':
      return state
    case 'initialised':
      break
    case 'valid':
      if (isEqual(prevSelections, selectionsInput)) {
        return state
      }
      break
    default:
      assertNever(prevState)
  }

  const selections = generateSelections<DataKey, DataRow>(selectionsInput, choicesMap, defaultSelections)

  if (selections == null) {
    errorLocal('stateReducerUpdateSelectionsAction null selections')
    return {
      ...state,
      state: 'invalid'
    }
  }

  const filters = generateFiltersFromChoices<DataKey, DataRow>({
    selections,
    choicesList,
    choicesMap,
    dimensionAxis,
    defaultFilters
  })
  if (filters == null) {
    errorLocal('stateReducerUpdateSelectionsAction null filters')
    return {
      ...state,
      state: 'invalid'
    }
  }

  return {
    ...state,
    state: 'valid',
    filters,
    selections,
    selectionsUpdated: !isEqual(defaultSelections, selections),
    zoomedIn: zoomedIn ?? false,
    firstAxisDomainDataKey,
    firstAxisDomain,
    secondAxisDomains: secondAxisDomains ?? [undefined, undefined]
  }
}

export const stateReducerUpdateDateRangeAction = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  state: ChartCardState<DataKey, DataRow>,
  { dateRange: dateRangeProp }: ChartCardStateUpdateDateRangeAction<DataKey, DataRow>
): ChartCardState<DataKey, DataRow> => {
  if (state.state === 'initialising' || state.state === 'invalid') {
    return state
  }

  const dateRange =
    dateRangeProp == null
      ? undefined
      : { startDate: new Date(dateRangeProp.startDate), endDate: new Date(dateRangeProp.endDate) }

  if (isEqual(state.dateRange, dateRange)) {
    return state
  }

  return {
    ...state,
    dateRange
  }
}

export const stateReducerSelectionAction = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  state: ChartCardState<DataKey, DataRow>,
  action: ChartCardStateSelectionAction<DataKey, DataRow>
): ChartCardState<DataKey, DataRow> => {
  if (state.state !== 'valid') {
    return state
  }

  const { selections, defaultSelections } = state
  const { type, id, item, items, others } = action
  const idForSelectionOthers = generateIdForSelectionOthers(id)
  const selectedOthers = selections[idForSelectionOthers] === SelectedOthers
  const selected = selections[id]
  switch (type) {
    case 'multipleAdd':
      return cascadeSelectionUpdate<DataKey, DataRow>({
        type,
        id,
        selection: [...(selected as MultipleSelectionValue<DataKey>), ...action.items],
        selectOthers: selectedOthers || others,
        state
      })
    case 'multipleRemove':
      return cascadeSelectionUpdate<DataKey, DataRow>({
        type,
        id,
        selection: (selected as MultipleSelectionValue<DataKey>).filter((value) => !items.includes(value as DataKey)),
        selectOthers: others ? false : selectedOthers,
        state
      })
    case 'multipleSet':
      return cascadeSelectionUpdate<DataKey, DataRow>({
        type,
        id,
        selection: items,
        selectOthers: others,
        state
      })
    case 'singleSet':
      return cascadeSelectionUpdate<DataKey, DataRow>({
        type,
        id,
        selection: item,
        selectOthers: undefined,
        state
      })
    case 'resetSelection':
      return cascadeSelectionUpdate<DataKey, DataRow>({
        type,
        id,
        selection: defaultSelections[id] as DefaultSelectionValue<DataKey>,
        selectOthers:
          idForSelectionOthers in defaultSelections
            ? defaultSelections[idForSelectionOthers] === SelectedOthers
            : undefined,
        state
      })
    default:
      assertNever(type)
  }
  return state
}

export const stateReducerZoomInAction = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  state: ChartCardState<DataKey, DataRow>,
  action: ChartCardStateZoomInAction<DataKey, DataRow>
): ChartCardState<DataKey, DataRow> => {
  if (state.state !== 'valid') {
    return state
  }

  let zoomedIn: ChartZoomedIn = false
  const { firstAxisDomain, firstAxisDomainDataKey, secondAxisDomains } = action

  if (firstAxisDomain != null) {
    if (secondAxisDomains == null) {
      zoomedIn = 'first'
    } else {
      zoomedIn = true
    }
  } else if (secondAxisDomains[0] != null || secondAxisDomains[1] != null) {
    zoomedIn = 'second'
  }

  return {
    ...state,
    zoomedIn,
    firstAxisDomainDataKey,
    firstAxisDomain,
    secondAxisDomains
  }
}

export const stateReducerResetZoomingAction = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  state: ChartCardState<DataKey, DataRow>,
  action: ChartCardStateResetZoomingAction<DataKey, DataRow>
): ChartCardState<DataKey, DataRow> => {
  if (state.state !== 'valid') {
    return state
  }

  return {
    ...state,
    zoomedIn: false,
    firstAxisDomain: undefined,
    secondAxisDomains: [undefined, undefined]
  }
}
